/**
 * WhatIsTBS - Explicação do Teor de Borracha Seca (TBS/DRC)
 * Fonte: IEA/SAA-SP, APABOR, Estudo CNA
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';

const WhatIsTBS = () => {
  const { t } = useTranslation();
  const [pesoUmido, setPesoUmido] = useState('');
  const [pesoSeco, setPesoSeco] = useState('');
  const [tbsCalculado, setTbsCalculado] = useState(null);

  const calcularTBS = () => {
    if (pesoUmido && pesoSeco && parseFloat(pesoUmido) > 0) {
      const tbs = (parseFloat(pesoSeco) / parseFloat(pesoUmido)) * 100;
      setTbsCalculado(tbs.toFixed(1));
    }
  };

  const limparCalculadora = () => {
    setPesoUmido('');
    setPesoSeco('');
    setTbsCalculado(null);
  };

  return (
    <div className="space-y-6">
      {/* O que é TBS */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800 flex items-center gap-2">
            📊 {t('whatIsTbsTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-700">
                <strong>TBS</strong> (Teor de Borracha Seca) / <strong>DRC</strong> (Dry Rubber Content) {t('tbsExplanation')}
              </p>
              <p className="text-gray-600 mt-2">
                {t('tbsExplanation2')}
              </p>
            </div>

            {/* Visual Explanation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-bold text-blue-800 mb-2">🧪 {t('practicalExample')}</h4>
                <p className="text-sm text-gray-700">
                  {t('practicalExampleText')}
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>53 kg</strong> {t('dryRubberValue')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span>
                    <span><strong>47 kg</strong> {t('waterMoisture')}</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-bold text-amber-800 mb-2">⚠️ {t('whyItMatters')}</h4>
                <p className="text-sm text-gray-700">
                  {t('whyItMattersText')}
                </p>
                <div className="mt-2 p-2 bg-white rounded">
                  <p className="text-xs text-gray-600">
                    <strong>{t('standardReference')}:</strong> 53% TBS
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Como o TBS afeta o preço */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800">💰 {t('howTbsAffectsPayment')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-600">
              {t('priceAdjustedByTbs')}
            </p>

            <div className="bg-gray-50 p-4 rounded-lg font-mono text-center">
              <p className="text-lg">
                <span className="text-gray-600">{t('adjustedPrice')} = </span>
                <span className="text-green-700">{t('basePrice')}</span>
                <span className="text-gray-600"> × (</span>
                <span className="text-blue-700">{t('yourTbs')}</span>
                <span className="text-gray-600"> ÷ </span>
                <span className="text-orange-700">53</span>
                <span className="text-gray-600">)</span>
              </p>
            </div>

            {/* Examples Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-green-100">
                    <th className="p-2 text-left">{t('yourTbs')}</th>
                    <th className="p-2 text-left">{t('basePrice')} (R$/kg)</th>
                    <th className="p-2 text-left">{t('factor')}</th>
                    <th className="p-2 text-left">{t('adjustedPrice')}</th>
                    <th className="p-2 text-left">{t('situation')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2">60%</td>
                    <td className="p-2">R$ 6,00</td>
                    <td className="p-2">60 ÷ 53 = 1,13</td>
                    <td className="p-2 text-green-700 font-bold">R$ 6,79</td>
                    <td className="p-2"><span className="text-green-600">↑ {t('aboveReference')}</span></td>
                  </tr>
                  <tr className="border-b bg-yellow-50">
                    <td className="p-2 font-bold">53%</td>
                    <td className="p-2">R$ 6,00</td>
                    <td className="p-2">53 ÷ 53 = 1,00</td>
                    <td className="p-2 font-bold">R$ 6,00</td>
                    <td className="p-2"><span className="text-gray-600">= {t('standardReferenceLabel')}</span></td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">46%</td>
                    <td className="p-2">R$ 6,00</td>
                    <td className="p-2">46 ÷ 53 = 0,87</td>
                    <td className="p-2 text-red-700 font-bold">R$ 5,21</td>
                    <td className="p-2"><span className="text-red-600">↓ {t('belowReference')}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 {t('tip')}:</strong> {t('tbsTip')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculadora de TBS */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800">🧮 {t('tbsCalculator')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              {t('tbsCalculatorDesc')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('wetWeight')}
                </label>
                <input
                  type="number"
                  value={pesoUmido}
                  onChange={(e) => setPesoUmido(e.target.value)}
                  placeholder="Ex: 100"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('wetWeightHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('dryWeight')}
                </label>
                <input
                  type="number"
                  value={pesoSeco}
                  onChange={(e) => setPesoSeco(e.target.value)}
                  placeholder="Ex: 53"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('dryWeightHint')}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={calcularTBS}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {t('calculateTbs')}
              </button>
              <button
                onClick={limparCalculadora}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('clear')}
              </button>
            </div>

            {tbsCalculado !== null && (
              <div className={`p-4 rounded-lg ${
                parseFloat(tbsCalculado) >= 53 ? 'bg-green-100 border border-green-300' : 'bg-amber-100 border border-amber-300'
              }`}>
                <div className="text-center">
                  <p className="text-sm text-gray-600">{t('yourCalculatedTbs')}</p>
                  <p className={`text-4xl font-bold ${
                    parseFloat(tbsCalculado) >= 53 ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    {tbsCalculado}%
                  </p>
                  <p className="text-sm mt-2">
                    {parseFloat(tbsCalculado) >= 53 
                      ? `✅ ${t('aboveOrEqualReference')}` 
                      : `⚠️ ${t('belowReferenceWarning')}`}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600">
                <strong>{t('note')}:</strong> {t('tbsNote')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatIsTBS;
